import { useCallback, useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import {
  getBumpOptions,
  transformBumpOptionsResponse,
  BumpOptionModel,
  getBumpOptionsSvcVas,
  transformBumpOptionsResponseSvcVas,
} from '@marketplace-web/vas/vas-data'

import { PreCheckoutModal } from '../../../../constants/bumps'
import useBumpCheckoutContext from '../../../../hooks/useBumpCheckoutContext'

const useBumpOptions = (activeModal: PreCheckoutModal) => {
  const { selectedItems } = useBumpCheckoutContext()

  const [selectedBumpOption, setSelectedBumpOption] = useState<BumpOptionModel | null>(null)
  const [isOptionsDataStale, setIsOptionsDataStale] = useState(false)

  const trackExpose = useTrackAbTestCallback()
  const vasApiGatewaySwapBumpsOptionsAbTest = useAbTest('vas_api_gateway_swap_bumps_options')
  const isVasApiGatewaySwapBumpsOptionsAbTestOn =
    vasApiGatewaySwapBumpsOptionsAbTest?.variant === 'on'

  const {
    fetch: fetchBumpOptionsCore,
    transformedData: bumpOptionsCore,
    isLoading: isBumpOptionsLoadingCore,
    error: bumpOptionsErrorCore,
  } = useFetch(getBumpOptions, transformBumpOptionsResponse)

  const {
    fetch: fetchBumpOptionsSvcVas,
    transformedData: bumpOptionsSvcVas,
    isLoading: isBumpOptionsLoadingSvcVas,
    error: bumpOptionsErrorSvcVas,
  } = useFetch(getBumpOptionsSvcVas, transformBumpOptionsResponseSvcVas)

  const bumpOptions = isVasApiGatewaySwapBumpsOptionsAbTestOn ? bumpOptionsSvcVas : bumpOptionsCore
  const isBumpOptionsLoading = isVasApiGatewaySwapBumpsOptionsAbTestOn
    ? isBumpOptionsLoadingSvcVas
    : isBumpOptionsLoadingCore
  const bumpOptionsError = isVasApiGatewaySwapBumpsOptionsAbTestOn
    ? bumpOptionsErrorSvcVas
    : bumpOptionsErrorCore
  const fetchBumpOptions = isVasApiGatewaySwapBumpsOptionsAbTestOn
    ? fetchBumpOptionsSvcVas
    : fetchBumpOptionsCore

  const updateSelectedBumpOption = useCallback(
    (optionsData: BumpOptionModel) => {
      setSelectedBumpOption(optionsData)
    },
    [setSelectedBumpOption],
  )

  useEffect(() => {
    setIsOptionsDataStale(true)
  }, [selectedItems])

  const handleFetchBumpOptions = useCallback(async () => {
    const response = await fetchBumpOptions({ itemIds: selectedItems.map(item => item.id) })

    trackExpose(vasApiGatewaySwapBumpsOptionsAbTest)

    if (!response.transformedData) return

    const defaultOption =
      response.transformedData.options.find(option => option.isDefault) ||
      response.transformedData.options[0]!

    setSelectedBumpOption(defaultOption)
    setIsOptionsDataStale(false)
  }, [fetchBumpOptions, selectedItems, trackExpose, vasApiGatewaySwapBumpsOptionsAbTest])

  useEffect(() => {
    if (activeModal === PreCheckoutModal.None) return
    if (activeModal === PreCheckoutModal.GenericErrorModal) return
    if (!isOptionsDataStale) return
    if (!selectedItems.length) return

    handleFetchBumpOptions()
  }, [handleFetchBumpOptions, selectedItems, isOptionsDataStale, activeModal])

  return {
    bumpOptions,
    isBumpOptionsLoading,
    bumpOptionsError,
    selectedBumpOption,
    updateSelectedBumpOption,
  }
}

export default useBumpOptions
